import './styles.css';
import { WorkoutEngine, type EngineEvent, type EngineSnapshot } from './engine';
import { diagram } from './diagrams';
import { formatTime, generateRoutine, LEVELS, MOVEMENT_INFO, routineDuration, type Step } from './routine';
import { repCueForElapsed } from './pacing';
import { selectedLevel } from './progression';
import { loadData, saveData, type AppData, type Effort, type Session } from './storage';
import { WorkoutSpeech } from './speech';

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
};
const testMode = new URLSearchParams(location.search).get('test') === '1';
let data: AppData = loadData();
let recommendation = selectedLevel(data.sessions, data.preferences.manualLevel);
let level = LEVELS[recommendation.level]!;
let steps: Step[] = generateRoutine(level, testMode);
let engine: WorkoutEngine;
let ticker = 0;
let currentStepIndex = -1;
let lastRepCueKey = '';
let timedCues = new Set<string>();
let startedAt = 0;
let feedbackSaved = false;
let wakeLock: { release: () => Promise<void>; released?: boolean } | null = null;

const views = Array.from(document.querySelectorAll<HTMLElement>('.view'));
const workoutView = $('#workout-view');
const progressBar = $('.progress-track');
const pauseButton = $('#pause-button');
const sectionsDialog = $('#sections-dialog') as HTMLDialogElement;
const endDialog = $('#end-dialog') as HTMLDialogElement;

const speech = new WorkoutSpeech(renderVoices);
speech.configure(data.preferences.muted, data.preferences.voiceURI, data.preferences.speechRate);

function showView(id: string): void {
  views.forEach((view) => { view.hidden = view.id !== id; });
  window.scrollTo({ top: 0, behavior: 'instant' });
  if (id !== 'workout-view') releaseWakeLock();
}

function refreshPlan(): void {
  recommendation = selectedLevel(data.sessions, data.preferences.manualLevel);
  level = LEVELS[recommendation.level]!;
  steps = generateRoutine(level, testMode);
  const minutes = Math.round(routineDuration(steps) / 60_000);
  $('#plan-label').textContent = `TODAY · ${level.name.toUpperCase()}`;
  $('#round-summary').textContent = `${level.rounds}-round`;
  $('#duration-summary').textContent = testMode ? 'short test' : `${minutes} min`;
  $('#plan-rounds').textContent = String(level.rounds);
  $('#plan-minutes').textContent = testMode ? '<1' : String(minutes);
  $('#why-level').textContent = `${level.name} · ${level.rounds} rounds`;
  $('#why-reason').textContent = recommendation.reason;
  $('#settings-reason').textContent = recommendation.reason;
  ($('#level-select') as HTMLSelectElement).value = data.preferences.manualLevel === null ? 'auto' : String(data.preferences.manualLevel);
}

function buildLevelOptions(): void {
  const select = $('#level-select') as HTMLSelectElement;
  LEVELS.forEach((item) => {
    const option = document.createElement('option');
    option.value = String(item.id);
    option.textContent = `${item.name} · ${item.rounds} rounds`;
    select.append(option);
  });
}

function renderVoices(voices: SpeechSynthesisVoice[]): void {
  const select = $('#voice-select') as HTMLSelectElement;
  const previous = data.preferences.voiceURI;
  select.replaceChildren(new Option('Browser default', ''));
  voices.filter((voice) => /^en[-_]/i.test(voice.lang)).forEach((voice) => {
    const option = new Option(`${voice.name} · ${voice.lang}${voice.localService ? ' · local' : ''}`, voice.voiceURI);
    select.add(option);
  });
  select.value = previous;
  const speechAvailable = 'speechSynthesis' in window && typeof window.speechSynthesis?.speak === 'function' && typeof window.SpeechSynthesisUtterance !== 'undefined';
  $('#speech-status').textContent = speechAvailable
    ? `${voices.length} browser voice${voices.length === 1 ? '' : 's'} found. Local English voices are preferred.`
    : 'Speech is unavailable here. All guidance remains visible and timers continue normally.';
}

function recreateEngine(): void {
  clearInterval(ticker);
  engine = new WorkoutEngine(steps, handleEngineEvent);
  currentStepIndex = -1;
  lastRepCueKey = '';
  timedCues.clear();
  feedbackSaved = false;
}

function handleEngineEvent(event: EngineEvent): void {
  if (event.type === 'step') cueNewStep(event.snapshot);
  if (event.type === 'complete') completeWorkout(event.snapshot);
  renderWorkout(event.snapshot);
}

function startWorkout(): void {
  refreshPlan();
  recreateEngine();
  startedAt = Date.now();
  showView('workout-view');
  const snapshot = engine.start();
  cueNewStep(snapshot);
  renderWorkout(snapshot);
  ticker = window.setInterval(tick, testMode ? 50 : 200);
  requestWakeLock();
}

function tick(): void {
  const before = engine.getSnapshot();
  const snapshot = engine.sync();
  renderWorkout(snapshot);
  if (snapshot.status === 'running' && snapshot.index === before.index && document.visibilityState === 'visible') emitPacedCue(snapshot);
}

function renderWorkout(snapshot: EngineSnapshot): void {
  const { step } = snapshot;
  if (snapshot.index !== currentStepIndex) {
    currentStepIndex = snapshot.index;
    lastRepCueKey = '';
    timedCues.clear();
    $('#diagram-wrap').innerHTML = diagram(step.movement);
  }
  workoutView.classList.toggle('paused', snapshot.status === 'paused');
  $('#round-label').textContent = step.kind === 'complete' ? 'ALL ROUNDS COMPLETE' : `ROUND ${step.round} OF ${step.rounds}`;
  $('#remaining-total').textContent = `${formatTime(snapshot.totalRemainingMs)} LEFT`;
  $('#progress-fill').style.width = `${snapshot.progress * 100}%`;
  progressBar.setAttribute('aria-valuenow', String(Math.round(snapshot.progress * 100)));
  $('#step-kind').textContent = kindLabel(step);
  $('#exercise-title').textContent = step.title;
  $('#form-cue').textContent = step.detail;
  const next = steps[snapshot.index + 1];
  $('#next-cue').textContent = next && next.kind !== 'complete' ? `NEXT · ${next.title.replace('Next: ', '').toUpperCase()}` : 'FINAL SECTION';

  if (step.kind === 'reps' && step.reps && step.cadenceMs) {
    const completed = Math.min(step.reps, Math.floor(snapshot.elapsedMs / step.cadenceMs));
    const remaining = Math.max(0, step.reps - completed);
    if (step.eachSide) {
      const side = completed % 2 === 0 ? 'LEFT' : 'RIGHT';
      $('#primary-count').textContent = String(Math.ceil(remaining / 2));
      $('#count-label').textContent = `EACH LEG REMAINING · ${side} NEXT`;
    } else {
      $('#primary-count').textContent = String(remaining);
      $('#count-label').textContent = 'REPS REMAINING';
    }
  } else {
    $('#primary-count').textContent = formatTime(snapshot.remainingMs);
    $('#count-label').textContent = snapshot.status === 'paused' ? 'PAUSED' : 'REMAINING';
  }
  const paused = snapshot.status === 'paused';
  pauseButton.innerHTML = paused ? '<span aria-hidden="true">▶</span><small>RESUME</small>' : '<span aria-hidden="true">Ⅱ</span><small>PAUSE</small>';
  pauseButton.setAttribute('aria-label', paused ? 'Resume workout' : 'Pause workout');
}

function kindLabel(step: Step): string {
  if (step.kind === 'reps') return 'CONTROLLED REPS';
  if (step.kind === 'timer') return step.movement === 'march' ? 'BRISK, NOT ALL-OUT' : 'STEADY HOLD';
  if (step.kind === 'prepare') return `${Math.ceil(step.durationMs / 1000)}-SECOND SETUP`;
  if (step.kind === 'rest') return 'RECOVER';
  return step.kind.toUpperCase();
}

function cueNewStep(snapshot: EngineSnapshot): void {
  lastRepCueKey = '';
  timedCues.clear();
  speech.speak(speech.cueForStep(snapshot), true);
}

function emitPacedCue(snapshot: EngineSnapshot): void {
  const { step, elapsedMs, remainingMs } = snapshot;
  if (step.kind === 'reps') {
    const cue = repCueForElapsed(step, elapsedMs);
    if (cue && cue.key !== lastRepCueKey) {
      lastRepCueKey = cue.key;
      speech.speak(cue.text);
    }
    return;
  }
  if (step.kind === 'timer' || step.kind === 'rest') {
    const ratio = step.durationMs ? elapsedMs / step.durationMs : 0;
    if (ratio >= .5 && !timedCues.has('half')) {
      timedCues.add('half');
      speech.speak(step.kind === 'rest' ? 'Halfway through rest.' : 'Halfway. Keep breathing.');
    }
    if (remainingMs <= 10_000 && step.durationMs > 15_000 && !timedCues.has('near')) {
      timedCues.add('near');
      speech.speak('Ten seconds. Stay controlled.');
    }
    const last = Math.ceil(remainingMs / 1000);
    if (last <= 3 && last > 0 && !timedCues.has(`count-${last}`)) {
      timedCues.add(`count-${last}`);
      speech.speak(String(last));
    }
  }
}

function completeWorkout(snapshot: EngineSnapshot): void {
  clearInterval(ticker);
  speech.speak(speech.cueForStep(snapshot), true);
  releaseWakeLock();
  showView('complete-view');
  document.querySelectorAll('.effort-grid button').forEach((button) => button.classList.remove('selected'));
  feedbackSaved = false;
}

function saveFeedback(effort: Effort): void {
  if (feedbackSaved) return;
  const session: Session = {
    id: crypto.randomUUID?.() ?? `${Date.now()}`,
    completedAt: new Date().toISOString(),
    level: level.id,
    rounds: level.rounds,
    durationSeconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
    effort,
  };
  data.sessions.push(session);
  data = { ...data, sessions: data.sessions.slice(-100) };
  saveData(data);
  feedbackSaved = true;
  document.querySelectorAll<HTMLButtonElement>('.effort-grid button').forEach((button) => button.classList.toggle('selected', button.dataset.effort === effort));
  refreshPlan();
  renderHistory();
}

function renderHistory(): void {
  const list = $('#history-list');
  if (!data.sessions.length) {
    list.innerHTML = '<div class="empty-state"><strong>No completed sessions yet</strong><p>Finish a workout and choose an effort rating to add it here.</p></div>';
    return;
  }
  list.replaceChildren(...data.sessions.slice().reverse().map((session) => {
    const item = document.createElement('div');
    item.className = 'history-item';
    const date = new Date(session.completedAt);
    const left = document.createElement('div');
    const right = document.createElement('div');
    right.className = 'history-effort';
    const strong = document.createElement('strong');
    strong.textContent = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const meta = document.createElement('span');
    meta.textContent = `${session.rounds} rounds · ${Math.max(1, Math.round(session.durationSeconds / 60))} min`;
    left.append(strong, meta);
    const effort = document.createElement('strong');
    effort.textContent = session.effort === 'right' ? 'About right' : session.effort;
    const plan = document.createElement('span');
    plan.textContent = LEVELS[session.level]?.name ?? 'Workout';
    right.append(effort, plan);
    item.append(left, right);
    return item;
  }));
}

function renderSections(): void {
  const list = $('#section-list');
  list.replaceChildren(...steps.map((step, index) => {
    if (step.kind === 'complete') return document.createDocumentFragment();
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.index = String(index);
    const title = document.createElement('span');
    title.textContent = step.title;
    const detail = document.createElement('small');
    detail.textContent = step.kind === 'reps' ? `${step.reps}${step.eachSide ? ' alternating' : ' reps'}` : formatTime(step.durationMs);
    button.append(title, detail);
    return button;
  }));
}

async function requestWakeLock(): Promise<void> {
  if (document.visibilityState !== 'visible' || engine?.getSnapshot().status !== 'running' || !('wakeLock' in navigator)) return;
  try {
    wakeLock = await (navigator as Navigator & { wakeLock: { request: (type: 'screen') => Promise<typeof wakeLock> } }).wakeLock.request('screen');
  } catch { wakeLock = null; }
}
async function releaseWakeLock(): Promise<void> {
  if (!wakeLock) return;
  try { await wakeLock.release(); } catch { /* Already released by browser. */ }
  wakeLock = null;
}

function persistPreferences(): void {
  saveData(data);
  speech.configure(data.preferences.muted, data.preferences.voiceURI, data.preferences.speechRate);
  refreshPlan();
}

$('#start-button').addEventListener('click', startWorkout);
$('#pause-button').addEventListener('click', () => {
  const snapshot = engine.getSnapshot();
  if (snapshot.status === 'running') { renderWorkout(engine.pause()); speech.cancel(); releaseWakeLock(); }
  else if (snapshot.status === 'paused') { renderWorkout(engine.resume()); cueNewStep(engine.getSnapshot()); requestWakeLock(); }
});
$('#prev-button').addEventListener('click', () => { const snap = engine.previous(); renderWorkout(snap); cueNewStep(snap); });
$('#next-button').addEventListener('click', () => { const snap = engine.next(); renderWorkout(snap); if (snap.status !== 'complete') cueNewStep(snap); });
$('#mute-button').addEventListener('click', () => {
  data.preferences.muted = !data.preferences.muted;
  $('#mute-button').textContent = data.preferences.muted ? 'VOICE OFF' : 'VOICE ON';
  $('#mute-button').setAttribute('aria-pressed', String(data.preferences.muted));
  if (data.preferences.muted) speech.cancel();
  persistPreferences();
});
$('#replay-button').addEventListener('click', () => speech.replay());
$('#sections-button').addEventListener('click', () => { renderSections(); sectionsDialog.showModal(); });
$('#section-list').addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-index]');
  if (!button) return;
  const snap = engine.jump(Number(button.dataset.index));
  renderWorkout(snap); cueNewStep(snap); sectionsDialog.close();
});
$('#end-button').addEventListener('click', () => { engine.pause(); releaseWakeLock(); endDialog.showModal(); });
$('#confirm-end').addEventListener('click', () => { engine.end(); speech.cancel(); showView('home-view'); });
$('#restart-button').addEventListener('click', () => { engine.restart(); startedAt = Date.now(); const snap = engine.start(); renderWorkout(snap); cueNewStep(snap); requestWakeLock(); });
endDialog.addEventListener('close', () => { if (endDialog.returnValue === 'cancel' && engine.getSnapshot().status === 'paused') { engine.resume(); requestWakeLock(); } });

document.querySelectorAll<HTMLButtonElement>('.effort-grid button').forEach((button) => button.addEventListener('click', () => saveFeedback(button.dataset.effort as Effort)));
$('#complete-home').addEventListener('click', () => showView('home-view'));
$('#history-button').addEventListener('click', () => { renderHistory(); showView('history-view'); });
$('#settings-button').addEventListener('click', () => showView('settings-view'));
$('#home-button').addEventListener('click', () => showView('home-view'));
$('#why-button').addEventListener('click', () => showView('why-view'));
document.querySelectorAll('[data-back]').forEach((button) => button.addEventListener('click', () => showView('home-view')));
$('#voice-select').addEventListener('change', (event) => { data.preferences.voiceURI = (event.target as HTMLSelectElement).value; persistPreferences(); speech.speak('This is your workout voice.', true); });
$('#rate-input').addEventListener('input', (event) => {
  data.preferences.speechRate = Number((event.target as HTMLInputElement).value);
  $('#rate-output').textContent = `${data.preferences.speechRate.toFixed(1)}×`;
  persistPreferences();
});
$('#level-select').addEventListener('change', (event) => {
  const value = (event.target as HTMLSelectElement).value;
  data.preferences.manualLevel = value === 'auto' ? null : Number(value);
  persistPreferences();
});
$('#easier-button').addEventListener('click', () => { data.preferences.manualLevel = Math.max(0, recommendation.level - 1); persistPreferences(); });
$('#harder-button').addEventListener('click', () => { data.preferences.manualLevel = Math.min(4, recommendation.level + 1); persistPreferences(); });
$('#auto-button').addEventListener('click', () => { data.preferences.manualLevel = null; persistPreferences(); });

document.addEventListener('visibilitychange', () => {
  if (!engine) return;
  if (document.visibilityState === 'hidden') { releaseWakeLock(); return; }
  const snapshot = engine.sync();
  renderWorkout(snapshot);
  if (snapshot.status === 'running') requestWakeLock();
});
window.addEventListener('online', () => { $('#offline-toast').hidden = true; });
window.addEventListener('offline', () => { $('#offline-toast').hidden = false; });

buildLevelOptions();
refreshPlan();
renderHistory();
$('#mute-button').textContent = data.preferences.muted ? 'VOICE OFF' : 'VOICE ON';
$('#mute-button').setAttribute('aria-pressed', String(data.preferences.muted));
($('#rate-input') as HTMLInputElement).value = String(data.preferences.speechRate);
$('#rate-output').textContent = `${data.preferences.speechRate.toFixed(1)}×`;
renderVoices(typeof speechSynthesis !== 'undefined' ? speechSynthesis.getVoices() : []);
if ('serviceWorker' in navigator && !testMode) window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => undefined));
